import { state } from './state.js';
import { openPlanComparisonModal } from './modal_manager.js';

let _publicConfigPromise = null;
let _gisInitStarted = false;
let _accountSummaryLoading = false;
let _authGateCloseBound = false;
let _authLoginClickBound = false;
let _preferredAuthOrigin = '';

const DOWNLOAD_GATE_PENDING_KEY = 'mosaic_pending_download_gate_v1';
const DOWNLOAD_GATE_PENDING_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const GUEST_CLAIM_TOKEN_KEY = 'mosaic_guest_claim_token_v1';
const POST_LOGIN_RETURN_KEY = 'mosaic_post_login_return_v1';
const POST_LOGIN_RETURN_MAX_AGE_MS = 6 * 60 * 60 * 1000;
const AUTH_GATE_DEFAULT_COPY = {
    title: 'Sign in to continue',
    subtitle: 'Sign in to download your mosaic and save your work.',
};
const AUTH_GATE_DOWNLOAD_COPY = {
    title: 'Sign in to download your mosaic',
    subtitle: 'Your first mosaic is ready. Sign in to unlock download and keep your work.',
};

function getCookie(name) {
    const raw = document.cookie || "";
    const parts = raw.split("; ");
    for (const part of parts) {
        if (!part) continue;
        const idx = part.indexOf("=");
        if (idx === -1) continue;
        const key = part.slice(0, idx);
        if (key === name) {
            return decodeURIComponent(part.slice(idx + 1));
        }
    }
    return "";
}

function getCsrfToken() {
    return getCookie("mosaic_csrf");
}

function loadPublicConfig() {
    if (_publicConfigPromise) return _publicConfigPromise;
    _publicConfigPromise = (async () => {
        try {
            const res = await fetch('/config');
            if (!res.ok) return null;
            const payload = await res.json();
            const configuredOrigin = String((payload && payload.oauth_login_origin) || '').trim();
            if (configuredOrigin) {
                _preferredAuthOrigin = configuredOrigin;
            }
            return payload;
        } catch (e) {
            return null;
        }
    })();
    return _publicConfigPromise;
}

function _authOrigin() {
    return _preferredAuthOrigin || (window.location.origin || '');
}

function _readGuestClaimToken() {
    try {
        return String(window.sessionStorage.getItem(GUEST_CLAIM_TOKEN_KEY) || '').trim();
    } catch (_e) {
        return '';
    }
}

function _writeGuestClaimToken(token) {
    const safe = String(token || '').trim();
    try {
        if (!safe) {
            window.sessionStorage.removeItem(GUEST_CLAIM_TOKEN_KEY);
            return;
        }
        window.sessionStorage.setItem(GUEST_CLAIM_TOKEN_KEY, safe);
    } catch (_e) {}
}

function _withGuestClaimToken(urlValue, options = {}) {
    const rawUrl = String(urlValue || '').trim() || '/auth/login';
    const token = _readGuestClaimToken();
    if (!token) return rawUrl;
    const absolute = Boolean(options.absolute);
    try {
        const base = window.location.origin || 'http://localhost';
        const parsed = new URL(rawUrl, base);
        parsed.searchParams.set('guest_claim_token', token);
        if (absolute) return parsed.toString();
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch (_e) {
        const joiner = rawUrl.includes('?') ? '&' : '?';
        return `${rawUrl}${joiner}guest_claim_token=${encodeURIComponent(token)}`;
    }
}

function _loginHrefWithGuestClaim() {
    const origin = _authOrigin();
    const loginBase = origin ? `${origin}/auth/login` : '/auth/login';
    return _withGuestClaimToken(loginBase, { absolute: Boolean(origin) });
}

function _syncLoginLinks() {
    const loginHref = _loginHrefWithGuestClaim();
    const anonLoginLink = document.querySelector('#authAnon .btn-login');
    if (anonLoginLink) anonLoginLink.setAttribute('href', loginHref);
    const fallback = document.getElementById('gisFallbackBtn');
    if (fallback) fallback.setAttribute('href', loginHref);
}

function clearPendingDownloadGateIntent() {
    try {
        localStorage.removeItem(DOWNLOAD_GATE_PENDING_KEY);
    } catch (_e) {}
}

function clearPostLoginReturnContext() {
    try {
        localStorage.removeItem(POST_LOGIN_RETURN_KEY);
    } catch (_e) {}
}

function _readCreateStepFromDom() {
    const active = document.querySelector('.create-step-pill.is-active[data-create-step]');
    if (!active) return '';
    return String(active.getAttribute('data-create-step') || '').trim().toLowerCase();
}

function _writePostLoginReturnContext(source = '') {
    const payload = {
        source: String(source || '').trim().toLowerCase(),
        saved_at: Date.now(),
        hash: String(window.location.hash || '').trim(),
        selected_history_job_id: String(state.selectedHistoryJobId || '').trim(),
        viewer_job_id: String(state.viewerJobId || state.jobId || '').trim(),
        create_step: _readCreateStepFromDom(),
        selected_source_photo_id: String(state.selectedSourcePhotoId || '').trim(),
        selected_library_id: String(state.selectedLibraryId || '').trim(),
    };
    try {
        localStorage.setItem(POST_LOGIN_RETURN_KEY, JSON.stringify(payload));
    } catch (_e) {}
}

export function consumePostLoginReturnContext() {
    let raw = null;
    try {
        raw = localStorage.getItem(POST_LOGIN_RETURN_KEY);
    } catch (_e) {
        return null;
    }
    if (!raw) return null;
    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (_e) {
        clearPostLoginReturnContext();
        return null;
    }
    const savedAt = Number((parsed && parsed.saved_at) || 0);
    if (!savedAt || (Date.now() - savedAt) > POST_LOGIN_RETURN_MAX_AGE_MS) {
        clearPostLoginReturnContext();
        return null;
    }
    clearPostLoginReturnContext();
    return {
        source: String((parsed && parsed.source) || '').trim().toLowerCase(),
        hash: String((parsed && parsed.hash) || '').trim(),
        selected_history_job_id: String((parsed && parsed.selected_history_job_id) || '').trim(),
        viewer_job_id: String((parsed && parsed.viewer_job_id) || '').trim(),
        create_step: String((parsed && parsed.create_step) || '').trim().toLowerCase(),
        selected_source_photo_id: String((parsed && parsed.selected_source_photo_id) || '').trim(),
        selected_library_id: String((parsed && parsed.selected_library_id) || '').trim(),
    };
}

export function peekPostLoginReturnContext() {
    let raw = null;
    try {
        raw = localStorage.getItem(POST_LOGIN_RETURN_KEY);
    } catch (_e) {
        return null;
    }
    if (!raw) return null;
    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (_e) {
        return null;
    }
    const savedAt = Number((parsed && parsed.saved_at) || 0);
    if (!savedAt || (Date.now() - savedAt) > POST_LOGIN_RETURN_MAX_AGE_MS) {
        return null;
    }
    return {
        source: String((parsed && parsed.source) || '').trim().toLowerCase(),
        hash: String((parsed && parsed.hash) || '').trim(),
        selected_history_job_id: String((parsed && parsed.selected_history_job_id) || '').trim(),
        viewer_job_id: String((parsed && parsed.viewer_job_id) || '').trim(),
        create_step: String((parsed && parsed.create_step) || '').trim().toLowerCase(),
        selected_source_photo_id: String((parsed && parsed.selected_source_photo_id) || '').trim(),
        selected_library_id: String((parsed && parsed.selected_library_id) || '').trim(),
    };
}

async function _navigateToLogin(options = {}) {
    const preservePendingDownload = Boolean(options && options.preservePendingDownload);
    const source = String((options && options.source) || '').trim().toLowerCase();
    _writePostLoginReturnContext(source);
    if (!preservePendingDownload) {
        clearPendingDownloadGateIntent();
    }
    try {
        await Promise.race([
            loadPublicConfig(),
            new Promise((resolve) => window.setTimeout(resolve, 900)),
        ]);
    } catch (_e) {}
    _syncLoginLinks();
    window.location.assign(_loginHrefWithGuestClaim());
}

function _bindAuthLoginLinks() {
    if (_authLoginClickBound) return;
    _authLoginClickBound = true;
    const anonLoginLink = document.querySelector('#authAnon .btn-login');
    if (anonLoginLink) {
        anonLoginLink.addEventListener('click', (event) => {
            if (event) event.preventDefault();
            _navigateToLogin({ preservePendingDownload: false, source: 'header' });
        });
    }
    const fallback = document.getElementById('gisFallbackBtn');
    if (fallback) {
        fallback.addEventListener('click', (event) => {
            if (event) event.preventDefault();
            const fromGate = Boolean(document.body && document.body.classList.contains('auth-gate-active'));
            _navigateToLogin({
                preservePendingDownload: fromGate,
                source: fromGate ? 'download_gate' : 'header',
            });
        });
    }
}

function loadGisScript() {
    if (window.google && window.google.accounts && window.google.accounts.id) {
        return Promise.resolve(true);
    }
    return new Promise((resolve) => {
        const existing = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
        if (existing) {
            existing.addEventListener('load', () => resolve(true));
            existing.addEventListener('error', () => resolve(false));
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.head.appendChild(script);
    });
}

async function initGisButton() {
    if (_gisInitStarted) {
        _syncLoginLinks();
        return;
    }
    _gisInitStarted = true;
    const container = document.getElementById('gisButton');
    const fallback = document.getElementById('gisFallbackBtn');
    _syncLoginLinks();
    if (container) {
        container.innerHTML = '';
        container.classList.add('hidden');
    }
    if (fallback) fallback.classList.remove('hidden');
}

function _authGateEls() {
    return {
        gate: document.getElementById('authGate'),
        title: document.getElementById('authGateTitle'),
        subtitle: document.getElementById('authGateSubtitle'),
        close: document.getElementById('authGateClose'),
    };
}

function _setAuthGateCopy(copy = AUTH_GATE_DEFAULT_COPY) {
    const els = _authGateEls();
    if (els.title) els.title.textContent = String((copy && copy.title) || AUTH_GATE_DEFAULT_COPY.title);
    if (els.subtitle) els.subtitle.textContent = String((copy && copy.subtitle) || AUTH_GATE_DEFAULT_COPY.subtitle);
}

function _readPendingDownloadGate() {
    let raw = null;
    try {
        raw = localStorage.getItem(DOWNLOAD_GATE_PENDING_KEY);
    } catch (_e) {
        return null;
    }
    if (!raw) return null;
    let parsed = null;
    try {
        parsed = JSON.parse(raw);
    } catch (_e) {
        try { localStorage.removeItem(DOWNLOAD_GATE_PENDING_KEY); } catch (_err) {}
        return null;
    }
    const savedAt = Number((parsed && parsed.saved_at) || 0);
    const jobId = String((parsed && parsed.job_id) || '').trim();
    if (!savedAt || !jobId) {
        try { localStorage.removeItem(DOWNLOAD_GATE_PENDING_KEY); } catch (_err) {}
        return null;
    }
    if ((Date.now() - savedAt) > DOWNLOAD_GATE_PENDING_MAX_AGE_MS) {
        try { localStorage.removeItem(DOWNLOAD_GATE_PENDING_KEY); } catch (_err) {}
        return null;
    }
    return { job_id: jobId, saved_at: savedAt };
}

function _writePendingDownloadGate(jobId) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) return;
    const payload = {
        job_id: safeJobId,
        saved_at: Date.now(),
    };
    try {
        localStorage.setItem(DOWNLOAD_GATE_PENDING_KEY, JSON.stringify(payload));
    } catch (_e) {}
}

function _bindAuthGateClose() {
    if (_authGateCloseBound) return;
    const { close } = _authGateEls();
    if (!close) return;
    _authGateCloseBound = true;
    close.addEventListener('click', (event) => {
        if (event) event.preventDefault();
        clearPendingDownloadGateIntent();
        clearPostLoginReturnContext();
        setAuthGate(false);
    });
}

function setAuthGate(active, options = {}) {
    const gate = document.getElementById('authGate');
    const body = document.body;
    const closeBtn = document.getElementById('authGateClose');
    const showClose = Boolean(options && options.showClose);
    if (active) {
        _setAuthGateCopy({
            title: String((options && options.title) || AUTH_GATE_DEFAULT_COPY.title),
            subtitle: String((options && options.subtitle) || AUTH_GATE_DEFAULT_COPY.subtitle),
        });
    } else {
        _setAuthGateCopy(AUTH_GATE_DEFAULT_COPY);
    }
    if (body) body.classList.toggle('auth-gate-active', Boolean(active));
    if (gate) {
        gate.classList.toggle('hidden', !active);
        gate.setAttribute('aria-hidden', String(!active));
    }
    if (closeBtn) {
        closeBtn.classList.toggle('hidden', !(active && showClose));
    }
}

export function consumePendingDownloadGateIntent() {
    const payload = _readPendingDownloadGate();
    if (!payload) return null;
    clearPendingDownloadGateIntent();
    return payload;
}

export function peekPendingDownloadGateIntent() {
    return _readPendingDownloadGate();
}

export function promptSignInForDownload(jobId = '') {
    const safeJobId = String(jobId || '').trim();
    if (safeJobId) {
        _writePendingDownloadGate(safeJobId);
    }
    _writePostLoginReturnContext('download_gate');
    _bindAuthLoginLinks();
    _syncLoginLinks();
    _bindAuthGateClose();
    setAuthLoading(false);
    setAuthGate(true, {
        title: AUTH_GATE_DOWNLOAD_COPY.title,
        subtitle: AUTH_GATE_DOWNLOAD_COPY.subtitle,
        showClose: true,
    });
    initGisButton();
}

function setAuthLoading(active) {
    const body = document.body;
    if (!body) return;
    body.classList.toggle('auth-loading', Boolean(active));
}

function extractApiErrorMessage(payload, fallback = 'Request failed.') {
    if (!payload) return fallback;
    if (typeof payload === 'string') return payload;
    const detail = payload.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object') {
        if (typeof detail.message === 'string') return detail.message;
        if (typeof detail.reason === 'string') return detail.reason;
    }
    if (typeof payload.message === 'string') return payload.message;
    if (typeof payload.error === 'string') return payload.error;
    return fallback;
}

function escapeHtml(value) {
    return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function formatInt(value, fallback = 0) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return String(fallback);
    return String(Math.floor(parsed));
}

function formatOptional(value, fallback = '—') {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
}

function formatIsoDatetime(value) {
    if (!value) return '—';
    try {
        const dt = new Date(String(value));
        if (!Number.isFinite(dt.getTime())) return String(value);
        return dt.toLocaleString();
    } catch (e) {
        return String(value);
    }
}

function planDisplayName(summary) {
    const raw = String((summary && summary.plan_name) || '').trim();
    if (raw) return raw;
    const tier = String((summary && summary.tier) || 'free').toLowerCase();
    if (tier === 'paid') return 'Paid';
    if (tier === 'pro') return 'Pro';
    return 'Free';
}

function subscriptionStatusDisplay(summary) {
    const subscription = (summary && summary.subscription) || {};
    const tier = String((summary && summary.tier) || 'free').toLowerCase();
    const subscriptionId = String(subscription.subscription_id || '').trim();
    const rawStatus = String(subscription.status || '').trim().toLowerCase();
    if (tier === 'free' && !subscriptionId) {
        return 'No paid subscription';
    }
    if (rawStatus === 'active' || rawStatus === 'trialing' || rawStatus === 'on_trial') {
        return 'Active';
    }
    if (rawStatus === 'past_due') return 'Payment due';
    if (rawStatus === 'none') return 'No paid subscription';
    if (rawStatus === 'cancelled' || rawStatus === 'expired' || rawStatus === 'inactive') {
        return 'Inactive';
    }
    if (!rawStatus) return 'Unknown';
    return rawStatus.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function authHeadersWithCsrf() {
    const headers = { 'Content-Type': 'application/json' };
    const csrf = getCsrfToken();
    if (csrf) headers['x-csrf-token'] = csrf;
    return headers;
}

function _accountSummaryModalEls() {
    return {
        modal: document.getElementById('accountSummaryModal'),
        sub: document.getElementById('accountSummarySub'),
        grid: document.getElementById('accountSummaryGrid'),
        close: document.getElementById('accountSummaryClose'),
    };
}

function _closeAccountSummaryModal() {
    const { modal } = _accountSummaryModalEls();
    if (!modal) return;
    modal.classList.add('hidden');
    modal.setAttribute('aria-hidden', 'true');
}

function _bindAccountSummaryModal() {
    const { modal, close } = _accountSummaryModalEls();
    if (!modal || !close || modal.dataset.bound === '1') return;
    modal.dataset.bound = '1';

    close.addEventListener('click', (event) => {
        event.preventDefault();
        _closeAccountSummaryModal();
    });

    modal.addEventListener('click', (event) => {
        if (event.target && event.target.classList && event.target.classList.contains('modal-backdrop')) {
            _closeAccountSummaryModal();
        }
    });

    document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (modal.classList.contains('hidden')) return;
        _closeAccountSummaryModal();
    });
}

function _summaryCard(title, lines = []) {
    const rendered = (lines || []).map((line) => (
        `<div class="account-summary-line"><span>${escapeHtml(line.label)}</span><strong>${escapeHtml(line.value)}</strong></div>`
    )).join('');
    return `<section class="account-summary-card"><h4>${escapeHtml(title)}</h4>${rendered}</section>`;
}

function _includedExportBuckets(quotas) {
    const result = {};
    const included = (quotas && quotas.included_exports) || {};
    const bySize = (included && included.by_size) || {};
    if (bySize && typeof bySize === 'object') {
        Object.entries(bySize).forEach(([key, value]) => {
            const row = (value && typeof value === 'object') ? value : {};
            const size = Number(row.size_px || key || 0);
            if (!Number.isFinite(size) || size <= 0) return;
            const total = Number(row.total || 0);
            const used = Number(row.used || 0);
            const remaining = Number(row.remaining || Math.max(0, total - used));
            result[Math.floor(size)] = {
                size_px: Math.floor(size),
                total: Math.max(0, Math.floor(total)),
                used: Math.max(0, Math.floor(used)),
                remaining: Math.max(0, Math.floor(remaining)),
            };
        });
    }
    if (Object.keys(result).length) return result;

    const monthly = (quotas && quotas.monthly_included_exports) || {};
    const size = Number(monthly.long_edge_px || 0);
    if (Number.isFinite(size) && size > 0) {
        result[Math.floor(size)] = {
            size_px: Math.floor(size),
            total: Math.max(0, Math.floor(Number(monthly.total || 0))),
            used: Math.max(0, Math.floor(Number(monthly.used || 0))),
            remaining: Math.max(0, Math.floor(Number(monthly.remaining || 0))),
        };
    }
    return result;
}

function _bucketLabel(row) {
    if (!row || Number(row.total || 0) <= 0) return 'Not included';
    return `${formatInt(row.used)} / ${formatInt(row.total)} used • ${formatInt(row.remaining)} left`;
}

function _renderAccountSummary(summary) {
    const { sub, grid } = _accountSummaryModalEls();
    if (!sub || !grid) return;

    if (!summary || typeof summary !== 'object') {
        sub.textContent = 'Could not load account details.';
        grid.innerHTML = '';
        return;
    }

    const subscription = summary.subscription || {};
    const quotas = summary.quotas || {};
    const usage = summary.usage || {};
    const dailyJobs = quotas.daily_jobs || {};
    const included = quotas.included_exports || {};
    const monthly = quotas.monthly_included_exports || {};
    const includedBuckets = _includedExportBuckets(quotas);
    const bucket3200 = includedBuckets[3200] || null;
    const bucket8000 = includedBuckets[8000] || null;
    const includedPeriodEnd = included.period_end || monthly.period_end || null;
    const planName = planDisplayName(summary);
    const subscriptionStatus = subscriptionStatusDisplay(summary);
    const renewal = formatIsoDatetime(subscription.renews_at || subscription.period_end);
    const isAdminUnlimited = Boolean(summary.admin_unlimited || ((quotas.downloads || {}).admin_unlimited));
    sub.textContent = `Plan: ${planName} • Subscription: ${subscriptionStatus}`;

    const dailyLimit = Number(dailyJobs.limit);
    const dailyRemaining = Number(dailyJobs.remaining);
    let dailyLabel = 'Unlimited';
    if (Number.isFinite(dailyLimit) && dailyLimit > 0) {
        dailyLabel = Number.isFinite(dailyRemaining)
            ? `${Math.max(0, Math.floor(dailyRemaining))} left`
            : `${Math.max(0, dailyLimit)} total`;
    }

    const cards = [
        _summaryCard('Plan', [
            { label: 'Current plan', value: planName },
            { label: 'Subscription status', value: subscriptionStatus },
            { label: 'Renews/resets', value: renewal },
            { label: 'Included direct download', value: `${formatInt(((quotas.downloads || {}).included_max_long_edge_px) || 0)} px` },
        ]),
        _summaryCard('Today', [
            { label: 'Started today', value: formatInt((usage.jobs || {}).started_today) },
            { label: 'Completed today', value: formatInt((usage.jobs || {}).completed_today) },
            { label: 'Remaining', value: dailyLabel },
            { label: 'Throttled', value: dailyJobs.is_throttled ? 'Yes' : 'No' },
        ]),
        _summaryCard('Library Limits', [
            { label: 'Libraries used', value: `${formatInt((usage.libraries || {}).total)} / ${formatOptional((quotas.libraries || {}).limit, 'Unlimited')}` },
            { label: 'Tiles total', value: formatInt((usage.libraries || {}).tiles_total) },
            { label: 'Tiles per library limit', value: formatOptional((quotas.tiles_per_library || {}).limit, 'Unlimited') },
            { label: 'Main uploads total', value: formatInt((usage.main_uploads || {}).total) },
        ]),
        _summaryCard('Export Allowance', [
            {
                label: 'Included direct download',
                value: isAdminUnlimited
                    ? 'All supported sizes (admin)'
                    : `${formatInt(((quotas.downloads || {}).included_max_long_edge_px) || 0)} px`,
            },
            { label: '3200px / month', value: isAdminUnlimited ? 'Unlimited (admin)' : _bucketLabel(bucket3200) },
            { label: '8000px / month', value: isAdminUnlimited ? 'Unlimited (admin)' : _bucketLabel(bucket8000) },
            { label: 'Period end', value: isAdminUnlimited ? 'N/A (admin)' : formatIsoDatetime(includedPeriodEnd) },
        ]),
        _summaryCard('Retention', [
            { label: 'Recent mosaics cap', value: formatOptional((quotas.retention || {}).recent_mosaics_limit, 'Unlimited') },
            { label: 'Retention window', value: `${formatOptional((quotas.retention || {}).retention_days, '—')} days` },
            { label: 'Current recent mosaics', value: formatInt(((quotas.retention || {}).current_recent_mosaics) || 0) },
            { label: 'Updated', value: formatIsoDatetime(summary.generated_at) },
        ]),
    ];

    grid.innerHTML = cards.join('');
}

async function _openAccountSummaryModal() {
    const { modal, sub, grid } = _accountSummaryModalEls();
    if (!modal || !sub || !grid) return;
    if (_accountSummaryLoading) return;
    _bindAccountSummaryModal();

    modal.classList.remove('hidden');
    modal.setAttribute('aria-hidden', 'false');
    sub.textContent = 'Loading account details...';
    grid.innerHTML = '';
    _accountSummaryLoading = true;
    try {
        const response = await fetch('/billing/account-summary', {
            method: 'GET',
            headers: authHeadersWithCsrf(),
            credentials: 'include',
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
            throw new Error(extractApiErrorMessage(payload, `Account summary failed (HTTP ${response.status}).`));
        }
        _renderAccountSummary(payload || {});
    } catch (e) {
        sub.textContent = (e && e.message) ? e.message : 'Could not load account details.';
        grid.innerHTML = '';
    } finally {
        _accountSummaryLoading = false;
    }
}

function configureUpgradeButton(plan) {
    const button = document.getElementById('btnUpgradePlan');
    if (!button) return;
    const entitlements = (plan && plan.entitlements) || {};
    const tier = String((entitlements.tier || 'free')).toLowerCase();

    let targetPlan = '';
    if (tier === 'free') targetPlan = 'paid';

    if (!targetPlan) {
        button.classList.add('hidden');
        button.onclick = null;
        return;
    }

    button.classList.remove('hidden');
    button.textContent = 'Upgrade';
    button.onclick = async (event) => {
        event.preventDefault();
        if (button.classList.contains('disabled')) return;
        button.classList.add('disabled');
        try {
            await openPlanComparisonModal({
                source: 'header_upgrade',
                preferredPlan: targetPlan,
                subtitle: targetPlan === 'paid'
                    ? 'Unlock larger direct exports and included monthly print exports.'
                    : 'Unlock your highest limits and priority queue with Pro.',
            });
        } finally {
            button.classList.remove('disabled');
        }
    };
}

function configureAccountButton(plan) {
    const button = document.getElementById('btnAccountPanel');
    if (!button) return;
    if (!plan) {
        button.classList.add('hidden');
        button.onclick = null;
        return;
    }
    button.classList.remove('hidden');
    button.onclick = async (event) => {
        event.preventDefault();
        await _openAccountSummaryModal();
    };
}

function applyPlanMeta(plan) {
    const planEl = document.getElementById('userPlan');
    const quotaEl = document.getElementById('userQuota');
    if (!planEl && !quotaEl) return;

    if (!plan) {
        if (planEl) planEl.textContent = '';
        if (quotaEl) quotaEl.textContent = '';
        configureAccountButton(null);
        configureUpgradeButton(null);
        return;
    }

    const planName = String(plan.name || 'Free');
    if (planEl) planEl.textContent = `Plan: ${planName}`;

    const dailyLimitRaw = plan.daily_job_limit;
    const dailyLimit = Number.isFinite(Number(dailyLimitRaw)) ? Number(dailyLimitRaw) : 0;
    const started = Number.isFinite(Number(plan.jobs_started_today)) ? Number(plan.jobs_started_today) : 0;
    let remaining = plan.remaining_jobs_today;
    if (!Number.isFinite(Number(remaining))) {
        remaining = Math.max(0, dailyLimit - started);
    }

    if (quotaEl) {
        const monthly = ((plan.entitlements || {}).monthly_included_exports || {});
        const monthlyTotal = Number(monthly.total || 0);
        const monthlyRemaining = Number(monthly.remaining || 0);
        const adminUnlimited = Boolean((plan.entitlements || {}).admin_unlimited);
        if (dailyLimit > 0) {
            quotaEl.textContent = `Jobs left today: ${remaining}`;
        } else {
            quotaEl.textContent = 'Jobs left today: Unlimited';
        }
        if (adminUnlimited) {
            quotaEl.textContent += ' • Print exports: unlimited';
        } else if (monthlyTotal > 0) {
            quotaEl.textContent += ` • Print exports: ${monthlyRemaining}/${monthlyTotal}`;
        }
        quotaEl.title = 'Jobs are counted when they start. Cancelling a job does not restore the daily quota.';
    }
    configureAccountButton(plan);
    configureUpgradeButton(plan);
}

export async function refreshUserConfig() {
    try {
        const res = await fetch('/config/me');
        if (!res.ok) return null;
        const plan = await res.json();
        state.plan = plan;
        document.dispatchEvent(new CustomEvent('plan:updated', { detail: { plan } }));
        state.planDailyJobLimit = Number.isFinite(Number(plan.daily_job_limit)) ? Number(plan.daily_job_limit) : null;
        state.planJobsStartedToday = Number.isFinite(Number(plan.jobs_started_today)) ? Number(plan.jobs_started_today) : 0;
        state.planRemainingJobs = Number.isFinite(Number(plan.remaining_jobs_today))
            ? Number(plan.remaining_jobs_today)
            : null;
        applyPlanMeta(plan);
        return plan;
    } catch (e) {
        return null;
    }
}

export async function initAuth() {
    // These IDs will be in the new HTML
    const anonDiv = document.getElementById('authAnon');
    const userDiv = document.getElementById('authUser');
    const avatar = document.getElementById('userAvatar');
    const name = document.getElementById('userName');
    const adminLink = document.getElementById('adminLink');
    let plan = null;

    setAuthLoading(true);
    _bindAuthGateClose();
    _bindAuthLoginLinks();
    _syncLoginLinks();
    try {
        const res = await fetch('/auth/me');
        if (!res.ok) throw new Error("Auth check failed");

        const user = await res.json();
        if (user.is_anonymous) {
            state.isAnonymous = true;
            state.userId = null;
            state.isAdmin = false;
            state.adminRole = null;
            _writeGuestClaimToken(String(user.guest_claim_token || ''));
            _syncLoginLinks();
            // SHOW LOGIN
            if (anonDiv) anonDiv.classList.remove('hidden');
            if (userDiv) userDiv.classList.add('hidden');
            if (adminLink) adminLink.classList.add('hidden');
            applyPlanMeta(null);
            setAuthGate(false);
            initGisButton();
            setAuthLoading(false);
            console.log("Auth: Anonymous");
        } else {
            state.isAnonymous = false;
            _writeGuestClaimToken('');
            _syncLoginLinks();
            // SHOW USER
            if (anonDiv) anonDiv.classList.add('hidden');
            if (userDiv) userDiv.classList.remove('hidden');
            setAuthGate(false);

            if (name) name.innerText = user.name || user.email;
            if (avatar && user.picture) avatar.src = user.picture;

            // UPDATE STATE to use the Verified Google ID
            state.userId = user.id;
            localStorage.setItem('mosaic_user_id', user.id);
            console.log("Auth: Logged in as", user.email);

            // Check admin access to toggle admin link
            state.isAdmin = false;
            state.adminRole = null;
            if (adminLink) {
                try {
                    const adminRes = await fetch('/admin/api/me', { credentials: 'include' });
                    if (adminRes.ok) {
                        adminLink.classList.remove('hidden');
                        state.isAdmin = true;
                        try {
                            const adminInfo = await adminRes.json();
                            state.adminRole = adminInfo && adminInfo.role ? adminInfo.role : null;
                        } catch (err) {
                            state.adminRole = null;
                        }
                    } else {
                        adminLink.classList.add('hidden');
                    }
                } catch (e) {
                    adminLink.classList.add('hidden');
                }
            } else {
                state.isAdmin = false;
                state.adminRole = null;
            }
            setAuthLoading(false);
        }
        if (!state.isAnonymous) {
            plan = await refreshUserConfig();
        }
    } catch (e) {
        state.isAnonymous = true;
        state.isAdmin = false;
        state.adminRole = null;
        _writeGuestClaimToken('');
        _syncLoginLinks();
        applyPlanMeta(null);
        setAuthGate(false);
        initGisButton();
        setAuthLoading(false);
        console.warn("Auth init failed", e);
    }
    return plan;
}

export async function logout(e) {
    if(e) e.preventDefault();
    try {
        // 1. Tell backend to kill the session cookie
        const headers = {};
        const csrfToken = getCsrfToken();
        if (csrfToken) headers["x-csrf-token"] = csrfToken;
        await fetch('/auth/logout', { method: 'POST', headers, credentials: 'include' });
    } catch(err) {
        console.error("Logout error", err);
    } finally {
        // 2. KILL THE LOCAL HEADER TOKEN (The "x-atr")
        localStorage.removeItem('mosaic_user_id');
        clearPendingDownloadGateIntent();
        clearPostLoginReturnContext();
        _writeGuestClaimToken('');
        _syncLoginLinks();

        // 3. Reset internal state
        state.userId = null;
        state.isAnonymous = true;

        // 4. Force reload to generate a fresh Anonymous ID
        window.location.href = "/";
    }
}
